/**
 * The companies that hold this journey — on the journey cards (header switcher
 * and "My journeys"): "Invited by" the company whose invitation or sponsorship
 * it came from, "Shared with" the company a citizen shared their own journey
 * with (`invited`, the server's `tierBadge.inviterAttributed`).
 *
 * Two companies that invite the citizen to the same destination share ONE
 * journey (D-19), so a card can carry several. It reads the way a person scans
 * a card:
 *   • one company — its logo, "Invited by {company}" and the relationship's
 *     state, as the card always showed;
 *   • several — their logos stacked (up to three, then "+N"), "Invited by
 *     {first} and N more"; hovering (or focusing) opens a card that lists every
 *     company with its logo, name and its own state. A screen reader hears
 *     every name in the line itself, so nothing hides behind the hover.
 *
 * Renders what the server sends (`journey.companies`, earliest relationship
 * first) and derives nothing.
 */
import { useLocale, useTranslation } from '@tonnie/locale';
import type { JourneyCompany } from '@tonnie/types';
import { HoverCard, HoverCardContent, HoverCardTrigger, cn } from '@tonnie/ui';
import type { TFunction } from 'i18next';

import { CompanyAvatar } from './CompanyAvatar';
import { JourneyRelationshipBadge } from './JourneyRelationshipBadge';

/**
 * The line that names the companies: "Invited by" when the journey came from a
 * company's invitation or sponsorship, "Shared with" when the citizen built the
 * journey and shared it (R5) — the server's `tierBadge.inviterAttributed`.
 * Either way the citizen sees every company that holds the journey. "My
 * journeys" names each company with this same line (JRN-SELECT-05).
 */
export function companiesLine(t: TFunction, invited: boolean, company: string): string {
  return invited
    ? t('journey.selector.invitedBy', { company })
    : t('journey.selector.sharedWith', { company });
}

/** Logos drawn before the rest collapse into "+N". */
const STACKED = 3;

interface IJourneyCompaniesProps {
  companies: readonly JourneyCompany[];
  /** Invited by them (true) or shared with them (false) — `tierBadge.inviterAttributed`. */
  invited: boolean;
  /** The relationship badge's test id — each surface keeps its own. */
  relationshipTestId: string;
  /**
   * Whether the line itself may take keyboard focus to open the list. Off
   * inside a menu item, whose roving focus a nested tab stop would break (the
   * full list is in the line's accessible name there).
   */
  focusable?: boolean;
  className?: string;
}

export function JourneyCompanies({
  companies,
  invited,
  relationshipTestId,
  focusable = true,
  className,
}: IJourneyCompaniesProps) {
  const { t } = useTranslation();
  const { formatList } = useLocale();
  if (companies.length === 0) return null;

  if (companies.length === 1) {
    const [company] = companies;
    return (
      <div
        className={cn('flex min-w-0 items-center gap-2', className)}
        data-testid="journey-company"
        data-company-tenant={company.tenantId}
      >
        <CompanyAvatar name={company.name} logoUrl={company.logoUrl} size="xs" />
        <span className="truncate text-xs text-muted-foreground">
          {companiesLine(t, invited, company.name)}
        </span>
        <JourneyRelationshipBadge relationship={company.relationship} testId={relationshipTestId} />
      </div>
    );
  }

  const [first] = companies;
  const shown = companies.slice(0, STACKED);
  const hidden = companies.length - shown.length;

  const summary = (
    <>
      <span className="flex shrink-0 -space-x-1.5" aria-hidden="true">
        {shown.map((company) => (
          <CompanyAvatar
            key={company.tenantId}
            name={company.name}
            logoUrl={company.logoUrl}
            size="xs"
            className="ring-2 ring-popover"
          />
        ))}
        {hidden > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded bg-muted px-1 text-2xs font-semibold text-muted-foreground ring-2 ring-popover">
            +{hidden}
          </span>
        )}
      </span>
      {/* The name may truncate; "and N more" never does — it is the point. */}
      <span
        className="flex min-w-0 items-baseline gap-1 text-xs text-muted-foreground"
        aria-hidden="true"
      >
        <span className="truncate">{companiesLine(t, invited, first.name)}</span>
        <span className="shrink-0 whitespace-nowrap">
          {t('journey.selector.andMore', { count: companies.length - 1 })}
        </span>
      </span>
      {/* Every name, for a screen reader — nothing hides behind the hover. */}
      <span className="sr-only">
        {companiesLine(t, invited, formatList(companies.map((company) => company.name)))}
      </span>
    </>
  );
  const triggerClass = cn(
    'flex min-w-0 items-center gap-2 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-ring',
    className
  );

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        {focusable ? (
          <button
            type="button"
            className={triggerClass}
            data-testid="journey-companies"
            data-company-count={companies.length}
          >
            {summary}
          </button>
        ) : (
          <span
            className={triggerClass}
            data-testid="journey-companies"
            data-company-count={companies.length}
          >
            {summary}
          </span>
        )}
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="w-72 p-0"
        data-testid="journey-companies-card"
        // Inside a menu, a click here must not choose the journey behind it.
        onClick={(event) => event.stopPropagation()}
      >
        <p className="border-b px-3 py-2 text-xs font-medium text-muted-foreground">
          {t('journey.selector.companiesTitle')}
        </p>
        <ul className="divide-y">
          {companies.map((company) => (
            <li
              key={company.tenantId}
              className="flex items-center gap-2.5 px-3 py-2.5"
              data-testid="journey-company"
              data-company-tenant={company.tenantId}
            >
              <CompanyAvatar name={company.name} logoUrl={company.logoUrl} size="sm" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{company.name}</span>
              <JourneyRelationshipBadge
                relationship={company.relationship}
                testId={relationshipTestId}
              />
            </li>
          ))}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}
